import { ActionContext, Module } from "vuex"
import { reportsClient } from "@/api/reportsClient"
import { InvoiceStatus, ISignUpReportPayload, ITariffPrices, TariffCode, TariffSubcriptionStatus } from "@/interfaces/reports"
import { IRootState, ITariffsState } from "@/interfaces/state"

const tariffModule: Module<ITariffsState, IRootState> = {
  state(): ITariffsState {
    return {
      tariffCode: TariffCode.TRIAL,
      tariffSubscriptionStatus: TariffSubcriptionStatus.EXPIRED,
      tariffExpiryDate: null,
      tariffPrices: null,
      invoiceStatus: null,
    }
  },
  getters: {
    getTariffCode(state: ITariffsState) {
      return state.tariffCode
    },
    getTariffSubscriptionStatus(state: ITariffsState) {
      return state.tariffSubscriptionStatus
    },
    getTariffExpiryDate(state: ITariffsState) {
      return state.tariffExpiryDate
    },
    getTariffPrices(state: ITariffsState) {
      return state.tariffPrices
    },
    invoiceStatus(state: ITariffsState) {
      return state.invoiceStatus
    },
  },
  mutations: {
    setTariffCode(state: ITariffsState, payload: { tariffCode: TariffCode }) {
      state.tariffCode = payload.tariffCode
    },
    setTariffSubscriptionStatus(state: ITariffsState, payload: { tariffSubscriptionStatus: TariffSubcriptionStatus }) {
      state.tariffSubscriptionStatus = payload.tariffSubscriptionStatus
    },
    setTariffExpiryDate(state: ITariffsState, payload: { tariffExpiryDate: string | Date | null }) {
      state.tariffExpiryDate = payload.tariffExpiryDate
        ? new Date(payload.tariffExpiryDate)
        : null;
    },
    setTariffPrices(state: ITariffsState, payload: { tariffPrices: ITariffPrices | null }) {
      state.tariffPrices = payload.tariffPrices
    },
    setInvoiceStatus(state: ITariffsState, payload: { invoiceStatus: InvoiceStatus }) {
      state.invoiceStatus = payload.invoiceStatus
    },
  },
  actions: {
    async autoTariffData(context: ActionContext<ITariffsState, ITariffsState>) {
      if (!localStorage.getItem('token')) {
        return
      }
      await context.dispatch("getTariffData")
    },
    async getTariffData(context: ActionContext<ITariffsState, ITariffsState>) {
      const getTariffResponse = await reportsClient.getUserTariff()
      context.commit('setTariffCode', { tariffCode: getTariffResponse.tariff_code })
      context.commit('setTariffSubscriptionStatus', { tariffSubscriptionStatus: getTariffResponse.tariff_subscription_status})
      context.commit('setTariffExpiryDate', { tariffExpiryDate: getTariffResponse.tariff_expiry_date ?? null })
    },
    async signUpReportTariff(context: ActionContext<ITariffsState, ITariffsState>, payload: ISignUpReportPayload) {
      const response = await reportsClient.signUpReport(payload.period)
      if (!response) {
        context.dispatch("openNotification", { keypath: "notifications.report_error" })
        return null
      }
      await context.dispatch("getTariffData")
      return response.payment_url
    },
    async cancelReportTariff(context: ActionContext<ITariffsState, ITariffsState>) {
      const response = await reportsClient.cancelReport()
      context.commit('setTariffExpiryDate', { tariffExpiryDate: response.expiry_date })
      await context.dispatch("getTariffData")
    },
    async reactivateReportTariff(context: ActionContext<ITariffsState, ITariffsState>) {
      const response = await reportsClient.reactivateReport()
      if (!response) {
        context.dispatch("openNotification", { keypath: "notifications.report_error" })
        return null
      }
      await context.dispatch("getTariffData")
    },
    async getTariffPrices(context: ActionContext<ITariffsState, ITariffsState>) {
      const response = await reportsClient.getPrices()
      if (response) {
        context.commit('setTariffPrices', { tariffPrices: response })
      }
    },
    async checkPaymentStatus(context: ActionContext<ITariffsState, ITariffsState>, invoice_id: number) {
      const response = await reportsClient.getPaymentStatus(invoice_id)
      if (response) {
        context.commit('setInvoiceStatus', { invoiceStatus: response.status })
        return response.status
      }
      return null
    },
  }
}

export default tariffModule
